import React from 'react';

import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

import { _config as envConfig } from '../config';
import { TFieldsFromTable } from '../types/types';

export class ToolBoxService {
  // Returns the server path.
  static getServerPath = () => {
    return envConfig.apiUrl;
  };

  static getConfig = (key: any) => {
    return (envConfig as any)[key];
  };

  // Removes all non-number and letter characters from a string.
  static removeSpecialCharacters = (input: any) => {
    return input?.toString()?.replace(/[^a-z0-9]+|\s+/gim, '');
  };

  // Will turn sOmeTHiNg into Something.
  static toCapitalCase = (input: any) => {
    return input.toString()[0].toUpperCase() + input.toString().substring(1).toLowerCase();
  };

  // Returns the first instance of an array or object.
  static firstOrAny = (obj: any) => {
    let thing;
    if (Array.isArray(obj)) {
      if (obj.length > 0) return obj[0];
    } else {
      for (const i in obj) {
        thing = i;
        break;
      }
    }
    return thing;
  };

  // Will take " AnyOldString" and clean it up till it's "Any Old String" ... or something like that.
  static cleanDisplayText = (input: any) => {
    return input
      .toString()
      .replace(/([A-Z])/g, ' $1')
      .trim();
  };

  // Get first string from React Fragment
  static getStringFromFragment = (input: any): string => {
    if (typeof input === 'string') return input;
    if (typeof input === 'number') return input + '';
    const elements = React.Children.toArray(input);
    const str1 = elements.find((child) => typeof child === 'string' || typeof child === 'number');
    return (str1 ?? '') + '';
  };

  // null, undefined, "", and NaN return false.
  static hasValue = (value: any) => {
    if (value || value === 0 || value === false) {
      return true;
    }
    return false;
  };

  // Tests if a string is a Guid.
  static isGuid = (toTest: any) => {
    if (!toTest) {
      return false;
    }
    const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (pattern.test(toTest)) {
      return true;
    }
    return false;
  };

  // Generates a random uuidv4 guid.
  static generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // Copies the values out of an object while making sure to pass them by value rather than by reference.
  static copyObject = (objectToCopy: any) => {
    return JSON.parse(JSON.stringify(objectToCopy));
  };

  // Verifies that a variable is an object with a value.
  static isObject = (toCheck: any) => {
    if (typeof toCheck === 'object' && toCheck !== null) {
      return true;
    }
    return false;
  };

  // Verifies that a value is numeric
  static isNumeric = (value: unknown) => {
    return (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && !isNaN(value as number);
  };

  // Generates a random string of a given length.
  static randomString = (length: any) => {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  };

  // This is a workaround so webapi does not interpret %2F(/) as part of the route.
  static urlComponentEncode = (toScrub: any) => {
    return encodeURIComponent(toScrub).replace('%2F', '%252F');
  };

  // Removes the current notification.
  static popNotification = () => {
    const elmntNotificationSection = document.getElementById('notification-section');
    const elmntNotificationMessage = document.getElementById('notification-message');
    if (elmntNotificationSection && elmntNotificationMessage) {
      elmntNotificationSection.className = 'hidden';
      elmntNotificationMessage.innerHTML = '';
    }
  };

  // Gathers the file name from a file path.
  static fileNameFromFilePath = (filePath: any) => {
    let valueToReturn = filePath;
    const listOfSuffixesToRemove = ['/thumbnail', '/download'];
    for (let i = 0; i < listOfSuffixesToRemove.length; i++) {
      const indexOf = valueToReturn.lastIndexOf(listOfSuffixesToRemove[i]);
      if (indexOf !== -1 && indexOf + listOfSuffixesToRemove[i].length === filePath.length) {
        valueToReturn = valueToReturn.substring(0, indexOf);
        i = listOfSuffixesToRemove.length;
      }
    }
    const lastDashIndex = valueToReturn.lastIndexOf('/');
    if (lastDashIndex !== -1) {
      valueToReturn = valueToReturn.substring(lastDashIndex + 1, valueToReturn.length);
      valueToReturn = valueToReturn.toUpperCase();
    }
    return valueToReturn;
  };

  // Gathers the file extension from a file name.
  static fileExtensionFromFileName = (fileName: any, defaultFileExtension = null) => {
    let valueToReturn = defaultFileExtension || '.txt';
    const lastPeriodIndex = fileName.lastIndexOf('.');
    if (lastPeriodIndex !== -1) {
      valueToReturn = fileName.substring(lastPeriodIndex, valueToReturn.length);
      valueToReturn = valueToReturn.toLowerCase();
    }
    return valueToReturn;
  };

  // Takes blob data and a filename and uses DOM manipulation to trigger a download
  // of the generated file.
  static downloadBlobData = (blobData: any, filename: any) => {
    const blobURL = window.URL.createObjectURL(blobData);
    const anchor = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.download = filename;
    anchor.href = blobURL;
    anchor.click();

    setTimeout(() => {
      document.body.removeChild(anchor);
    }, 500);
  };

  // A toString method for nullabel boolean values.
  static boolToText = (boolValue: any) => {
    return boolValue ? 'Yes' : 'No';
  };

  // Parses a JSON Web Token.
  static parseJwt = (token: string | undefined) => {
    if (typeof token === 'string') {
      const base64Url = token.split('.')[1] ?? '';
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/').trim();
      return ToolBoxService.parseJson(window.atob(base64)) ?? {};
    }
    return {};
  };

  static parseJson(json: string) {
    try {
      return JSON.parse(json);
    } catch (e) {
      return undefined;
    }
  }

  // Gathers a particular query paramter as referenced by name from the current page's url.
  static gatherQueryParameter = (parameterName: any) => {
    return (this as any).gatherURLParams()[parameterName] || null;
  };

  // Updates a particular url query paramter as referenced by name in the current page's url.  By default this is done so that a new history record is not added to preserve browser back button functionality without polluting it with parameter changes.
  static updateQueryParameter = (parameterName: any, value: any, newHistoryRecord = false) => {
    newHistoryRecord = newHistoryRecord || false;
    const allURLParams: any = this.gatherURLParams();
    allURLParams[parameterName] = value;
    this.updateURLParameters(allURLParams, newHistoryRecord);
  };

  // Gathers either an object or a string of the url paramters containted in the page's url.
  static gatherURLParams = (asString = false) => {
    let queryString = window.location.search;
    let queryParameters: any = {};
    if (this.hasValue(queryString)) {
      queryString = queryString.substring(1); // Removes the leading "?"
      if (asString) {
        return queryString;
      } else {
        queryParameters = JSON.parse(
          '{"' + decodeURI(queryString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}',
        );
        for (const param in queryParameters) {
          queryParameters[param] = decodeURIComponent(queryParameters[param]);
        }
      }
    }
    return queryParameters;
  };

  // Sets the current page's url parameters for easier reloading without losing the current filters and selecitons or forcing a reload.  By default this is done so that a new history record is not added to preserve browser back button functionality without polluting it with parameter changes.
  static updateURLParameters = (queryParameters: any, newHistoryRecord = false) => {
    const currentQueryString = this.gatherURLParams(true);
    const newQueryString = this.generateNewQueryString(queryParameters);

    // Verify that something would change.
    if (currentQueryString !== newQueryString) {
      if (newHistoryRecord) {
        window.history.pushState(
          null,
          document.title,
          window.location.origin + window.location.pathname + '?' + newQueryString,
        );
      } else {
        window.history.replaceState(
          null,
          document.title,
          window.location.origin + window.location.pathname + '?' + newQueryString,
        );
      }
    }
  };

  // Takes in an array and turns it into a query string for display in the page url.
  static generateNewQueryString = (params: any) => {
    const rawQueryString = params
      .map((param: any) => {
        return `${this.removeSpecialCharacters(param.name)}${param.operator}${param.value}`;
      })
      .join('&');

    return encodeURI(rawQueryString);
  };

  // Gathers details regarding the current page from the url.
  static currentPage = (endNodeOnly = false) => {
    let valueToReturn = window.location.pathname;
    if (endNodeOnly) {
      const lastDashIndex = valueToReturn.lastIndexOf('/');
      if (lastDashIndex !== -1) {
        valueToReturn = valueToReturn.substring(lastDashIndex + 1, valueToReturn.length);
        valueToReturn = this.toCapitalCase(valueToReturn);
      }
    }
    return valueToReturn;
  };

  // Returns the domain from the current url.
  static currentDomain = () => {
    return window.location.hostname.toUpperCase();
  };

  // Takes in data type describing a database table's field and returns something useful for the front end application.
  static translateDataTypeForFrontEnd = (type: any) => {
    let toReturn = undefined as any;
    switch (type) {
      case 'datetime2':
        toReturn = 'datetime';
        break;
      case 'date':
        toReturn = 'date';
        break;
      case 'int':
      case 'smallint':
        toReturn = 'number';
        break;
      case 'decimal':
        toReturn = 'decimal';
        break;
      case 'nvarchar':
      case 'text':
      default:
        toReturn = 'text';
        break;
    }
    return toReturn;
  };

  // Takes in a table details object and returns the fields.
  static generateFieldsFromTableDetails = (tableDetails: any, tableName: any): TFieldsFromTable[] => {
    let fields: any = [];
    tableDetails.forEach((table: any) => {
      fields = fields.concat(
        table.Columns.map((column: any) => {
          const field = {
            TableName: table.Name,
            FieldName: column.Name,
            Type: this.translateDataTypeForFrontEnd(column.Type),
            Required: column.Required || false,
            MaxLength: column.MaxLength || null,
            Operator: '%',
            FilterString: null,
            DisplayName: column.FriendlyName || column.Name,
            Display: column.IsVisible || false,
          };
          this.setFieldDisplayDefaults(field, tableName);
          return field;
        }),
      );
    });
    return fields;
  };

  // Calculates and sets the name to display for a given field (for columns, filters, and such) and if the field should be displayed by default.
  static setFieldDisplayDefaults = (field: any, tableName: any) => {
    if (field.TableName === tableName) {
      // field.DisplayName = field.FieldName;
    } else {
      field.DisplayName = `${field.TableName}.${field.FieldName}`;
    }
  };

  // Takes a table's fields and returns the data required for a search's "SelectFields" input based on which fields should be displayed.
  static determineFieldsToDisplay = (rawFields: any) => {
    const fields = [] as any;
    rawFields.forEach((field: any) => {
      fields.push({
        TableName: field.TableName,
        FieldName: field.FieldName,
      });
    });
    return fields;
  };

  // Gathers the filters from the page url's query parameters to update table fields' data.
  static updateFiltersFromQueryParameters = (fields: any) => {
    const params = this.gatherURLParams();
    for (const param in params) {
      const indexOf = fields.findIndex((x: any) => {
        if (x.DisplayName === param) {
          return true;
        }
        if (`${x.DisplayName}>` === param || `${x.DisplayName}<` === param) {
          return true;
        }
      });
      if (indexOf !== -1) {
        switch (fields[indexOf].Type) {
          case 'text':
            fields[indexOf].FilterString = params[param];
            break;
          case 'number':
          case 'decimal':
            if (param.includes('>')) {
              fields[indexOf].Operator = '>=';
              fields[indexOf].FilterNumber = params[param];
            } else if (param.includes('<')) {
              fields[indexOf].Operator = '<=';
              fields[indexOf].FilterNumber = params[param];
            } else {
              const values = params[param].replace(/[()]/g, '').split(',');
              fields[indexOf].Operator = '-';
              fields[indexOf].FilterNumber = values[0];
              fields[indexOf].FilterNumber2 = values[1];
            }
            break;
          case 'datetime':
          case 'date':
            if (param.includes('>')) {
              fields[indexOf].Operator = '>=';
              fields[indexOf].FilterDate = params[param];
            } else if (param.includes('<')) {
              fields[indexOf].Operator = '<=';
              fields[indexOf].FilterDate = params[param];
            } else {
              const values = params[param].replace(/[()]/g, '').split(',');
              fields[indexOf].Operator = '-';
              fields[indexOf].FilterDate = values[0];
              fields[indexOf].FilterDate2 = values[1];
            }
            break;
          default:
            break;
        }
      }
    }
  };

  // Takes in a list of fields and returns the column name to display for a given table.
  static getColumnsFromFields = (fields: any) => {
    return fields.filter((x: any) => x.Display).map((y: any) => y.DisplayName);
  };

  // Gathers the active filters from the table fields.
  static getActiveFilters = (fields: any) => {
    return fields.filter((x: any) => {
      if (
        x.FilterString ||
        x.FilterNumber ||
        (x.FilterNumber && x.FilterNumber2) ||
        x.FilterDate ||
        (x.FilterDate && x.FilterDate2) ||
        x.FilterBool === true ||
        x.FilterBool === false ||
        x.FilterGuid
      ) {
        return x;
      }
    });
  };

  static splitPascalCase(word: string) {
    if (!word) return '';
    const wordRe = /($[a-z])|[A-Z][^A-Z]+/g;
    return word.match(wordRe)?.join(' ') ?? '';
  }

  // Takes in a date, datetime, or a string representation of a date or datetime, and returns a formatted string.
  static formatAsDate = (date: any, format = 'YYYY-MM-DD') => {
    return dayjs(date).format(format);
  };

  //takes in an array of sizes (S, M, L, XL) and puts it in specified order (asc or desc)
  static sortSizes = (sizes: string[], order = 'asc') => {
    if (!Array.isArray(sizes) || (order.toLowerCase() !== 'asc' && order.toLowerCase() !== 'desc')) {
      throw new Error("Sizes need to be passed in as an array and order needs to be 'asc' or 'desc'");
    }
    const uppercaseSizes = sizes.map((size) => (size = size.toUpperCase()));

    const sortedSizeValues = [
      'XS',
      'S',
      'SM',
      'SMALL',
      'M',
      'MD',
      'MEDIUM',
      'L',
      'LG',
      'LARGE',
      'XL',
      'X-LARGE',
      'XXL',
      'XX-LARGE',
    ].filter((x) => new Set(uppercaseSizes).has(x));

    if (order === 'desc') {
      return sortedSizeValues.reverse();
    }

    return sortedSizeValues;
  };
  // COLOR CONVERSTIONS

  static hexToRgba = (hex: any) => {
    if (!hex) return;

    const cleanedHexValue = hex.replace('#', '');

    const r = parseInt(cleanedHexValue.substr(0, 2), 16);
    const g = parseInt(cleanedHexValue.substr(2, 2), 16);
    const b = parseInt(cleanedHexValue.substr(4, 2), 16);
    const a = Number(Math.abs(parseInt(cleanedHexValue.substr(6, 2), 16) / 255).toPrecision(2));

    if (a > -1) {
      return { r, g, b, a };
    }

    return { r, g, b, a: 1 };
  };

  static rgbaToHex = ({ r, g, b, a }: any) => {
    if (!r || !g || !b) return;

    const outParts = [r.toString(16), g.toString(16), b.toString(16)];

    if (a > -1 && a !== 1) {
      outParts.push(
        Math.round(a * 255)
          .toString(16)
          .substring(0, 2),
      );
    }

    // Pad single-digit output values
    outParts.forEach(function (part, i) {
      if (part.length === 1) {
        outParts[i] = '0' + part;
      }
    });

    return '#' + outParts.join('');
  };

  static setLocalStorageData = (item: any) => {
    //save data to local storage for creating a cart item after punchout
    const key = uuidv4();
    localStorage.setItem(key, JSON.stringify(item));

    return key;
  };

  static setLocalStorageDataWithKey = (item: unknown, key: string) => {
    //save data to local storage for creating a cart item after punchout
    localStorage.setItem(key, JSON.stringify(item));
    return key;
  };

  static preventEnterKeySubmission = (e: React.KeyboardEvent<HTMLElement>) => {
    const { key, target } = e;
    if (key !== 'Enter' || target instanceof HTMLTextAreaElement) {
      return;
    }
    e.preventDefault();
  };

  // Remove falsy elements from array, which include an empty string "", 0, NaN, null, undefined, and false
  static cleanArray<T>(arr: T[]) {
    return arr.filter(Boolean);
  }

  static deepCopy = (obj: any) => {
    return JSON.parse(JSON.stringify(obj));
  };

  static objectsAreEqual(obj1: Record<string, any>, obj2: Record<string, any>): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }
    return true;
  }
}
